import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, ShoppingBag, Loader2, Lock } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import MerchHeader from '../components/MerchHeader';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAYPAL_HOSTED_BUTTON_ID = 'GKVW63N7PCF6U';

const Cart = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { items, updateQty, removeItem, subtotal, count, clear } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!items.length) return;
    if (!form.name || !form.email || !form.phone) {
      toast({ title: 'Missing info', description: 'Please fill in name, email, and phone.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/orders`, {
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        notes: form.notes,
        items: items.map((i) => ({
          product_slug: i.slug,
          product_name: i.name,
          size: i.size || '',
          price: i.price,
          quantity: i.quantity,
        })),
      });
      const order = res.data;
      clear();
      // Open PayPal in new tab
      const params = new URLSearchParams({
        hosted_button_id: PAYPAL_HOSTED_BUTTON_ID,
        item_name: `ACT Merch Order #${order.id.slice(0, 8)}`,
        custom: `Order ${order.id}`,
      });
      window.open(
        `https://www.paypal.com/donate/?${params.toString()}`,
        '_blank',
        'noopener,noreferrer'
      );
      navigate(`/merch/order/${order.id}`);
    } catch (err) {
      toast({
        title: 'Order failed',
        description: 'Please try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f4ee]">
      <MerchHeader />

      <main className="max-w-[1200px] mx-auto px-4 md:px-6 py-14">
        <h1
          className="text-[38px] md:text-[52px] text-[#0a1030] leading-tight mb-10"
          style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
        >
          Your Cart
        </h1>

        {items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-14 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#f4f2ef] flex items-center justify-center mb-4">
              <ShoppingBag className="h-6 w-6 text-[#0a1030]" />
            </div>
            <h3
              className="text-2xl text-[#0a1030]"
              style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
            >
              Your cart is empty
            </h3>
            <p className="text-sm text-[#5a5a5a] mt-1 mb-6">Add a shirt or hat to get started.</p>
            <Button
              asChild
              className="rounded-full bg-[#0a1030] hover:bg-[#111a4a] text-white px-8 h-12"
            >
              <Link to="/merch">Browse merch</Link>
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-10">
            {/* Items */}
            <div className="lg:col-span-3 bg-white rounded-2xl border border-black/5 shadow-sm p-4 md:p-6 space-y-4">
              {items.map((i) => (
                <div key={`${i.slug}-${i.size}`} className="flex gap-4 items-center border-b border-black/5 last:border-b-0 pb-4 last:pb-0">
                  <div className="h-20 w-20 md:h-24 md:w-24 rounded-lg overflow-hidden bg-[#f4f2ef] flex-shrink-0">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[16px] md:text-[18px] text-[#0a1030] leading-tight"
                      style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
                    >
                      {i.name}
                    </div>
                    <div className="text-xs text-[#7a7a7a] mt-0.5">Size {i.size}</div>
                    <div className="mt-2 flex items-center gap-4">
                      <div className="inline-flex items-center border border-black/15 rounded-md">
                        <button
                          onClick={() => updateQty(i.slug, i.size, Math.max(1, i.quantity - 1))}
                          className="w-8 h-8 text-[#3a3a3a] hover:bg-[#f4f2ef] rounded-l-md"
                        >
                          −
                        </button>
                        <div className="w-9 text-center text-sm font-semibold">{i.quantity}</div>
                        <button
                          onClick={() => updateQty(i.slug, i.size, i.quantity + 1)}
                          className="w-8 h-8 text-[#3a3a3a] hover:bg-[#f4f2ef] rounded-r-md"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(i.slug, i.size)}
                        className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
                      >
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="font-semibold text-[#0a1030] whitespace-nowrap">
                    ${(i.price * i.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>

            {/* Checkout */}
            <form onSubmit={handleCheckout} className="lg:col-span-2 bg-white rounded-2xl border border-black/5 shadow-sm p-6 md:p-8 h-fit lg:sticky lg:top-24">
              <h2
                className="text-[22px] md:text-[26px] text-[#0a1030] leading-tight"
                style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}
              >
                Checkout
              </h2>

              <div className="mt-5 space-y-4">
                <div>
                  <Label htmlFor="name" className="text-[12px] font-semibold">Full name*</Label>
                  <Input id="name" value={form.name} onChange={(e) => update('name', e.target.value)} className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[12px] font-semibold">Email*</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[12px] font-semibold">Phone*</Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="mt-1 h-11" />
                </div>
                <div>
                  <Label htmlFor="notes" className="text-[12px] font-semibold">Notes <span className="text-[#8a8478] font-normal">(optional)</span></Label>
                  <Textarea id="notes" rows={2} value={form.notes} onChange={(e) => update('notes', e.target.value)} className="mt-1 resize-none" />
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-black/10 space-y-2 text-sm">
                <div className="flex justify-between text-[#5a5a5a]">
                  <span>Items ({count})</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#5a5a5a]">
                  <span>Pickup at church</span>
                  <span>Free</span>
                </div>
                <div className="flex justify-between text-[18px] font-semibold text-[#0a1030] pt-2">
                  <span>Total</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="mt-6 w-full rounded-full bg-[#1c2b6b] hover:bg-[#141f52] text-white h-12 text-[15px] font-semibold"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Placing order…</>
                ) : (
                  'Place order & pay with PayPal'
                )}
              </Button>

              <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#7a7a7a]">
                <Lock className="h-3 w-3" /> Payment handled securely by PayPal
              </div>
            </form>
          </div>
        )}
      </main>

      <Footer />
      <Toaster />
    </div>
  );
};

export default Cart;
